"use client";

import * as React from "react";

/**
 * The article chrome's words — the font popover, the share popover, the comment
 * drawer and the thread's toasts.
 *
 * These components are shared by every product, and the products are NOT all in
 * the same language: the main site, EJ Education, EJ Tech, LJ, EJ Health and EJ
 * Monthly are Chinese, and EJ Insight is EJ's English edition. The strings used
 * to be Chinese literals, which was wrong only for EJ Insight.
 *
 * Routing them through the app's `next-intl` catalogue looked like the fix and
 * was not: it made the words follow the URL LOCALE, so the Chinese products
 * rendered English chrome at `/en` — a change QA had not asked for and did not
 * want ("this english localization should only apply within ejinsight, the
 * other brands should remain unchanged").
 *
 * So the choice is by PRODUCT, not by locale. Chinese is the default; EJ
 * Insight's layout wraps its subtree in the English set. Nothing else changes,
 * at any locale.
 */
export type ArticleChromeStrings = {
  font: Record<string, string>;
  share: Record<string, string>;
  comments: Record<string, string>;
};

export const ARTICLE_CHROME_ZH: ArticleChromeStrings = {
  font: {
    smaller: "縮小字型",
    larger: "放大字型",
    small: "小",
    standard: "標準",
    large: "大",
    xlarge: "特大",
    heading: "字型大小",
    sample: "軟件股過去廿多年炙手可熱，硬件板塊近日逞強。",
  },
  share: {
    title: "分享此文章",
    close: "關閉",
    email: "電郵",
    link: "文章連結",
    copy: "複製連結",
    copied: "已複製連結",
    copiedToast: "連結已複製到剪貼簿",
    linkHeading: "文章連結",
    copiedShort: "已複製",
    copyShort: "複製",
  },
  comments: {
    close: "關閉",
    placeholder: "發表你的評論…",
    commentLabel: "評論內容",
    send: "發送評論",
    reply: "回覆此評論",
    replyPlaceholder: "撰寫你的回覆…",
    replyLabel: "回覆內容",
    replySend: "發送回覆",
    replyingTo: "回覆 @{author}",
    edit: "修改評論",
    editPlaceholder: "修改你的評論…",
    editSave: "儲存修改",
    discard: "取消草稿",
    delete: "刪除",
    deleteAria: "刪除評論",
    deleteTitle: "刪除評論？",
    deleteBody: "評論刪除後將無法復原，其他讀者亦不會再看到這則評論。",
    report: "檢舉此評論",
    reportReason: "檢舉原因",
    reportOtherLabel: "其他檢舉原因",
    reportOtherPlaceholder: "請說明原因…",
    reasonSpam: "垃圾訊息或廣告",
    reasonHate: "仇恨言論或騷擾",
    reasonFalse: "不實資訊",
    reasonViolence: "暴力或危險內容",
    reasonOther: "其他",
    toastPosted: "留言已發布",
    toastReplied: "回覆已發布",
    toastEdited: "留言已更新",
    toastDeleted: "留言已刪除",
    toastReported: "已提交檢舉，我們會盡快跟進",
    toastReportNeedsReason: "請選擇檢舉原因",
    title: "評論",
    titleCount: "評論（{count}）",
    sort: "排序",
    sortAria: "排序方式：{label}",
    edited: "（已編輯：{time}）",
    deleted: "此評論已被刪除",
    likeAria: "讚好，目前 {count} 人讚好",
    replyShort: "回覆",
    editShort: "修改",
    deleteShort: "刪除",
    reportShort: "檢舉",
    reportTitle: "檢舉評論",
    sortNewest: "最新",
    sortOldest: "最舊",
    sortReplies: "最多回覆",
    sortLikes: "最多讚好",
    submit: "提交",
    cancel: "取消",
    reportSubmit: "提交檢舉",
  },
};

export const ARTICLE_CHROME_EN: ArticleChromeStrings = {
  font: {
    smaller: "Smaller text",
    larger: "Larger text",
    small: "S",
    standard: "M",
    large: "L",
    xlarge: "XL",
    heading: "Text size",
    sample: "Software stocks have run hot for two decades; hardware is having its moment.",
  },
  share: {
    title: "Share this article",
    close: "Close",
    email: "Email",
    link: "Article link",
    copy: "Copy link",
    copied: "Link copied",
    copiedToast: "Link copied to clipboard",
    linkHeading: "Article link",
    copiedShort: "Copied",
    copyShort: "Copy",
  },
  comments: {
    close: "Close",
    placeholder: "Add a comment…",
    commentLabel: "Comment",
    send: "Post comment",
    reply: "Reply to this comment",
    replyPlaceholder: "Write a reply…",
    replyLabel: "Reply",
    replySend: "Post reply",
    replyingTo: "Replying to @{author}",
    edit: "Edit comment",
    editPlaceholder: "Edit your comment…",
    editSave: "Save changes",
    discard: "Discard draft",
    delete: "Delete",
    deleteAria: "Delete comment",
    deleteTitle: "Delete this comment?",
    deleteBody: "Deleting a comment cannot be undone, and other readers will no longer see it.",
    report: "Report this comment",
    reportReason: "Reason for reporting",
    reportOtherLabel: "Other reason",
    reportOtherPlaceholder: "Tell us more…",
    reasonSpam: "Spam or advertising",
    reasonHate: "Hate speech or harassment",
    reasonFalse: "False information",
    reasonViolence: "Violent or dangerous content",
    reasonOther: "Other",
    toastPosted: "Comment posted",
    toastReplied: "Reply posted",
    toastEdited: "Comment updated",
    toastDeleted: "Comment deleted",
    toastReported: "Report submitted — we'll follow up shortly",
    toastReportNeedsReason: "Choose a reason first",
    title: "Comments",
    titleCount: "Comments ({count})",
    sort: "Sort",
    sortAria: "Sort by: {label}",
    edited: " (edited {time})",
    deleted: "This comment was deleted",
    likeAria: "Like — {count} so far",
    replyShort: "Reply",
    editShort: "Edit",
    deleteShort: "Delete",
    reportShort: "Report",
    reportTitle: "Report comment",
    sortNewest: "Newest",
    sortOldest: "Oldest",
    sortReplies: "Most replies",
    sortLikes: "Most liked",
    submit: "Submit",
    cancel: "Cancel",
    reportSubmit: "Submit report",
  },
};

const ChromeStringsContext = React.createContext<ArticleChromeStrings>(
  ARTICLE_CHROME_ZH,
);

export function ArticleChromeStringsProvider({
  strings,
  children,
}: {
  strings: ArticleChromeStrings;
  children: React.ReactNode;
}) {
  return (
    <ChromeStringsContext.Provider value={strings}>
      {children}
    </ChromeStringsContext.Provider>
  );
}

/**
 * `t("key")` for one group, with `{placeholder}` interpolation — the same call
 * shape the components already used, so adopting this was a one-line change per
 * component rather than a rewrite.
 */
export function useChromeStrings(group: "font" | "share" | "comments") {
  const strings = React.useContext(ChromeStringsContext);
  return React.useCallback(
    (key: string, values?: Record<string, string | number>) => {
      const raw = strings[group][key] ?? key;
      if (!values) return raw;
      return Object.entries(values).reduce(
        (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
        raw,
      );
    },
    [strings, group],
  );
}
